import React, { useMemo, useState } from "react";
import { api, BRAND } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Upload, Printer, QrCode, Save } from "lucide-react";

const initial = {
  child_name: "",
  date_of_birth: "",
  emergency_contact: "",
  phone_number: "",
  diagnosis: "Autism Spectrum Disorder",
  communication_needs: "",
  allergies: "",
  notes: "",
  photo_data_url: "",
};

// Positions are relative percentages on the template
// Template is 1:1; left photo box, fields on right.
const FIELD_POS = {
  photo:        { left: "5%", top: "30%", width: "26%", height: "35%" },
  qr:           { left: "84%", top: "82%", width: "11%", height: "16%" },
};

export default function IDCardBuilder() {
  const { user } = useApp();
  const [form, setForm] = useState(initial);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);

  const onUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4_500_000) { toast.error("Image too large (max 4.5MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, photo_data_url: reader.result }));
    reader.readAsDataURL(f);
  };

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSave = async () => {
    if (!form.child_name) { toast.error("Child's name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/idcards", form);
      setSaved(data);
      toast.success("ID card saved! Share the QR or print it.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save card");
    } finally { setSaving(false); }
  };

  const emergencyUrl = useMemo(() => saved ? `${window.location.origin}/emergency/${saved.card_id}` : "", [saved]);
  const qrSrc = emergencyUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(emergencyUrl)}`
    : `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent("https://zizisworld.com")}`;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="idcard-builder-page">
      <div className="mb-8">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Custom Autism ID Card</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Upload your child's photo, fill the safe-template fields, and generate a printable card plus a QR code linking to a private emergency profile page.
        </p>
        {!user && (
          <p className="mt-3 text-sm text-purple-700 bg-purple-50 inline-block px-3 py-1.5 rounded-full">
            Tip: <Link to="/login" className="font-semibold underline">Sign in</Link> to save cards to your dashboard and earn 50 pts.
          </p>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        {/* FORM */}
        <div className="card-soft p-6 space-y-4">
          <div>
            <Label>Child Photo</Label>
            <label className="mt-2 flex items-center gap-3 border-2 border-dashed border-pink-200 rounded-2xl p-4 cursor-pointer hover:bg-pink-50">
              <Upload className="h-5 w-5 text-pink-500" />
              <span className="text-sm text-slate-700">{form.photo_data_url ? "Change photo" : "Upload photo (JPG/PNG)"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="idcard-photo-input" />
            </label>
          </div>
          {[
            ["child_name", "Child's Name *", "Jane Doe"],
            ["date_of_birth", "Date of Birth", "MM/DD/YYYY"],
            ["emergency_contact", "Emergency Contact (Name)", "Mom — Maria Doe"],
            ["phone_number", "Phone Number", "(555) 123-4567"],
            ["diagnosis", "Diagnosis / Condition", "Autism Spectrum Disorder"],
            ["communication_needs", "Communication Needs", "Non-verbal, uses AAC"],
            ["allergies", "Allergies", "Peanuts, Latex"],
          ].map(([k, label, ph]) => (
            <div key={k}>
              <Label>{label}</Label>
              <Input data-testid={`idcard-${k}`} value={form[k]} onChange={onChange(k)} placeholder={ph} />
            </div>
          ))}
          <div>
            <Label>Notes / Special Instructions</Label>
            <Textarea data-testid="idcard-notes" rows={3} value={form.notes} onChange={onChange("notes")} placeholder="Sensory sensitivities, calming strategies, medications, etc." />
          </div>
          <button data-testid="idcard-save-btn" onClick={onSave} disabled={saving} className="btn-pink w-full flex items-center justify-center gap-2">
            <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save & Generate"}
          </button>
        </div>

        {/* PREVIEW */}
        <div>
          <div className="card-soft p-4 printable" data-testid="idcard-preview">
            <div
              className="idcard-canvas"
              style={{ backgroundImage: `url(${BRAND.idCardTemplate})` }}
            >
              {form.photo_data_url ? (
                <div className="photo-box" style={{ ...FIELD_POS.photo, backgroundImage: `url(${form.photo_data_url})` }} />
              ) : (
                <div className="photo-box" style={FIELD_POS.photo}>
                  <div className="w-full h-full grid place-items-center text-pink-500 text-xs text-center px-2 font-semibold">Upload<br/>Photo</div>
                </div>
              )}
              <div className="field" style={{ left: "46%", top: "32%", width: "50%", fontSize: "clamp(9px, 1.2vw, 14px)" }}>{form.child_name}</div>
              <div className="field" style={{ left: "50%", top: "38.5%", width: "44%", fontSize: "clamp(8px, 1.1vw, 13px)" }}>{form.date_of_birth}</div>
              <div className="field" style={{ left: "58%", top: "44.5%", width: "36%", fontSize: "clamp(8px, 1vw, 12px)" }}>{form.emergency_contact}</div>
              <div className="field" style={{ left: "53%", top: "50.5%", width: "41%", fontSize: "clamp(8px, 1.1vw, 13px)" }}>{form.phone_number}</div>
              <div className="field" style={{ left: "56.5%", top: "56.5%", width: "37%", fontSize: "clamp(8px, 1vw, 12px)" }}>{form.diagnosis}</div>
              <div className="field" style={{ left: "59%", top: "62.5%", width: "34%", fontSize: "clamp(7px, 0.9vw, 11px)" }}>{form.communication_needs}</div>
              <div className="field" style={{ left: "47%", top: "68.5%", width: "46%", fontSize: "clamp(8px, 1vw, 12px)" }}>{form.allergies}</div>
              <div className="field" style={{ left: "66%", top: "74.5%", width: "28%", fontSize: "clamp(7px, 0.85vw, 10px)", whiteSpace: "normal", transform: "translateY(-90%)" }}>{form.notes}</div>
              <div className="qr-box" style={FIELD_POS.qr}>
                <img src={qrSrc} alt="QR" className="w-full h-full" />
              </div>
            </div>
          </div>

          {saved && (
            <div className="card-soft p-4 mt-4" data-testid="idcard-saved">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold"><QrCode className="h-4 w-4" /> Card saved</div>
              <div className="text-sm text-slate-700 mt-2">Public emergency profile:</div>
              <a href={emergencyUrl} target="_blank" rel="noreferrer" className="text-pink-600 break-all text-sm font-semibold">{emergencyUrl}</a>
              <button onClick={() => window.print()} className="btn-outline-pink mt-3 inline-flex items-center gap-2" data-testid="idcard-print-btn">
                <Printer className="h-4 w-4" /> Print Card
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
